import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ListItemText,
  Menu,
  ListItemIcon
} from '@mui/material';
import { 
  InfoOutlined, 
  DragIndicator,
  DateRange,
  Search as SearchIcon, 
  Star, 
  StarBorder,
  Delete,
  FileDownload,
  Print as PrintIcon,
  NoteAdd as AddendumIcon,
  Send as SendIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowRight as ArrowRightIcon
} from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import { DataGrid, GRID_DEFAULT_LOCALE_TEXT } from '@mui/x-data-grid';
import { apiFetch, createApiUrl, getApiBaseUrl } from '../../utils/apiConfig';
import { getAuthHeaders } from '../../utils/authUtils';
import { openInViewerWindow } from '../../utils/viewerWindow';
import CustomDatePicker from '../CustomDatePicker';
import { formatDateSync, formatDateWithAgeSync, formatDateTimeSync, useFormatConfig } from '../../utils/dateTimeFormat';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../CentralSnackbar';
import ColumnOrderDialog from './ColumnOrderDialog';
import DraggableColumnHeader, { reorderColumnFields } from './DraggableColumnHeader';
import TaskSearchBar from './filters/TaskSearchBar';
import TaskFilterDialog from './filters/TaskFilterDialog';
import {
  applyTaskFilters,
  chipsToSearchText,
  extractSearchTerms,
  formatConditionChipLabel,
  searchTextToChips
} from './filters/filterEngine';
import { useTaskQueryFilters } from '../../hooks/useTaskQueryFilters';
import PDFPreviewDialog from './PDFPreviewDialog';
import PDFPreviewDialogStructured from './PDFPreviewDialogStructured';
import DicomPDFViewer from './DicomPDFViewer';
import '../../style.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

/** Vollbild: nur Seiteninhalt unter der App Bar (gleiches Muster wie Approval) */
const SEARCH_PAGE_CONTENT_ZOOM = 0.8;
const SHOW_COLUMN_ORDER_DIALOG = false;
const ALL_REPORT_STATUSES = ['NEW', 'STARTED', 'PROVISIONALLY', 'AWAITING_APPROVAL', 'APPROVED', 'SIGNED', 'REJECTED'];
const SEARCH_RESULT_FIELDS = [
  'report_status', 'exam_status', 'patient_name', 'birth_date', 'patient_id', 'accession_number',
  'modality', 'study_description', 'approved_by', 'approval_time', 'signed_by_names',
  'report_approved_at', 'comment', 'scan_protocol'
];

const Search = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useSnackbar();
  const { isLoaded: isDateFormatLoaded } = useFormatConfig(); // Stelle sicher, dass Datumsformat geladen ist
  const saveTimeout = useRef(null);
  
  const {
    searchChips,
    setSearchChips,
    addSearchChip,
    removeSearchChip,
    conditions,
    setConditions,
    validConditions,
    hasActiveConditions,
    loadConditions,
    clearConditions,
    removeCondition,
    filterDialogOpen,
    filterAnchorEl,
    openFilterDialog,
    closeFilterDialog
  } = useTaskQueryFilters();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [results, setResults] = useState([]);
  const filteredResults = useMemo(
    () => applyTaskFilters(results, { searchChips: [], conditions, searchFields: SEARCH_RESULT_FIELDS }),
    [results, conditions]
  );
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  
  // Settings für Spaltenbreiten und Sichtbarkeit
  const [searchSettings, setSearchSettings] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showColumnOrderDialog, setShowColumnOrderDialog] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const [showDateRangeDialog, setShowDateRangeDialog] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  
  // PDF Preview - für Drucken im Kontextmenü (Template-Typ-bewusst)
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewStructuredOpen, setPdfPreviewStructuredOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [highlightTerms, setHighlightTerms] = useState([]);

  // Debug State-Änderungen
  useEffect(() => {
    if (pdfPreviewOpen) {
      console.log('📝 🟢 STATE CHANGE: pdfPreviewOpen = true (Freitext Dialog öffnet sich)');
    }
  }, [pdfPreviewOpen]);

  useEffect(() => {
    if (pdfPreviewStructuredOpen) {
      console.log('📋 🟢 STATE CHANGE: pdfPreviewStructuredOpen = true (Strukturiert Dialog öffnet sich)');
    }
  }, [pdfPreviewStructuredOpen]);
  
  // DICOM PDF Viewer - für Test
  const [dicomPdfOpen, setDicomPdfOpen] = useState(false);
  const [dicomPdfReportId, setDicomPdfReportId] = useState(null);
  
  // Viewer-States
  const [currentUser, setCurrentUser] = useState(null);
  const [viewerConfig, setViewerConfig] = useState({});
  const [viewerDataLoaded, setViewerDataLoaded] = useState(false); // NEUER FLAG
  
  // Kontextmenü
  const [contextMenu, setContextMenu] = useState(null);
  
  // AI Summary im Kontextmenü
  const [aiUserPrompts, setAiUserPrompts] = useState([]);
  const [aiSubMenuAnchor, setAiSubMenuAnchor] = useState(null);
  
  // Addendum Dialog
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);
  const [addendumReportId, setAddendumReportId] = useState(null);
  const [addendumExamId, setAddendumExamId] = useState(null);
  const [addendumIsCombined, setAddendumIsCombined] = useState(false);
  
  // Gespeicherte Suchen
  const [savedSearches, setSavedSearches] = useState([]);
  const [defaultSearchId, setDefaultSearchId] = useState(null);
  const [selectedSearchId, setSelectedSearchId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // AI Settings laden
  useEffect(() => {
    const loadAiSettings = async () => {
      try {
        const response = await fetch(createApiUrl('profile/ai-settings'), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setAiUserPrompts(data.ai_settings?.prompts || []);
        }
      } catch (e) {
        console.error('Error loading AI settings for search:', e);
      }
    };
    loadAiSettings();
  }, []);

  // Lade Viewer-Konfiguration und Benutzer-Präferenz beim Start
  useEffect(() => {
    // NUR EINMAL beim Mount ausführen
    if (viewerDataLoaded) return;
    
    loadViewerData();
  }, []); // LEERES DEPENDENCY ARRAY - nur beim Mount!

  const loadViewerData = async () => {
    // Verhindere mehrfache parallele Aufrufe
    if (viewerDataLoaded) {
      console.log('🔄 DEBUG: Viewer data bereits geladen, überspringe...');
      return;
    }

    try {
      const headers = getAuthHeaders();
      
      if (!headers.Authorization) {
        console.log('No auth token available, skipping viewer data load');
        return;
      }

      // Load current user data
      const userResponse = await fetch(`${API_BASE}/auth/me`, {
        headers: headers,
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('✅ DEBUG: User data loaded successfully:', {
          username: userData.username,
          viewer_preference: userData.viewer_preference,
          hasViewerPreference: !!userData.viewer_preference
        });
        
        setCurrentUser(userData);
      } else {
        console.log('❌ DEBUG: Failed to load user data:', userResponse.status);
        return;
      }

      // Load viewer config
      const configResponse = await fetch(`${API_BASE}/profile/viewer-config`, {
        headers: headers,
      });

      if (configResponse.ok) {
        const configData = await configResponse.json();
        if (configData.success) {
          setViewerConfig(configData.viewer_config);
        }
      } else {
        console.log('Failed to load viewer config:', configResponse.status);
      }
      
      // Markiere als geladen - WICHTIG: Nur bei Erfolg!
      setViewerDataLoaded(true);
      console.log('✅ DEBUG: Viewer data loading completed');
      
    } catch (error) {
      console.error('Error loading viewer data:', error);
    }
    // KEIN finally Block mehr - Loading Flag wird in den einzelnen Pfaden gesetzt
  };

  // Automatische Suche bei Änderung der Filter
  useEffect(() => {
    // Debounce: Nur suchen, wenn mindestens ein Suchkriterium vorhanden ist
    const timeoutId = setTimeout(() => {
      const searchText = chipsToSearchText(searchChips);
      if (searchText || startDate || endDate) {
        performSearch();
      }
    }, 500); // 500ms Debounce

    return () => clearTimeout(timeoutId);
  }, [searchChips, startDate, endDate]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const searchText = chipsToSearchText(searchChips);
      if (searchText) params.append('search_text', searchText);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('limit', '500');

      const response = await fetch(
        createApiUrl(`reports/search/all?${params.toString()}`),
        {
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
        setTotalCount(data.total_count || 0);
        setHasMore(data.has_more || false);
        
        // Dialog anzeigen, wenn mehr als 500 Ergebnisse
        if (data.has_more) {
          setShowWarningDialog(true);
        }
      } else {
        console.error('Search failed:', response.status);
      }
    } catch (error) {
      console.error('Error searching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search Settings laden
  const fetchSearchSettings = async () => {
    try {
      const response = await fetch(createApiUrl('profile/search-settings'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSearchSettings(data.search_settings || null);
      }
    } catch (err) {
      console.error('Error fetching search settings:', err);
      setSearchSettings(null);
    }
  };

  // Search Settings speichern
  const saveSearchSettings = async (newSettings) => {
    try {
      const response = await fetch(createApiUrl('profile/search-settings'), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ search_settings: newSettings })
      });
      
      if (response.ok) {
        setSearchSettings(newSettings);
      }
    } catch (err) {
      console.error('Error saving search settings:', err);
    }
  };

  // Debounce-Speichern
  const debounceSave = (newSettings) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      saveSearchSettings(newSettings);
    }, 500);
  };

  // Search Settings laden beim Initialisieren
  useEffect(() => {
    fetchSearchSettings();
  }, []);

  // Settings auf State anwenden, wenn sie geladen wurden
  useEffect(() => {
    if (searchSettings !== null && !isInitialized) {
      setIsInitialized(true);
      
      // Spaltenbreiten anwenden
      if (searchSettings.columnWidths) {
        setColumnWidths(searchSettings.columnWidths);
      }
      
      // Spaltensichtbarkeit anwenden
      if (searchSettings.columnVisibility) {
        setColumnVisibilityModel(searchSettings.columnVisibility);
      }
      
      // Spaltenreihenfolge anwenden
      if (searchSettings.columnOrder) {
        setColumnOrder(searchSettings.columnOrder);
      }
      
      // Prüfen ob Standard-Suchen bereits existieren
      const today = new Date().toISOString().split('T')[0];
      let searches = searchSettings.savedSearches || [];
      
      // Standard-Suchen IDs
      const todaySearchId = 'search-today';
      const thisWeekSearchId = 'search-this-week';
      const thisMonthSearchId = 'search-this-month';
      
      let hasChanges = false;
      
      // 1. "Heute" Suche
      let todaySearch = searches.find(s => s.id === todaySearchId);
      if (!todaySearch) {
        todaySearch = {
          id: todaySearchId,
          name: t('search.savedSearches.today', 'Heute'),
          searchChips: [],
          conditions: [],
          searchText: '',
          startDate: 'TODAY',
          endDate: 'TODAY',
          isDynamicToday: true
        };
        searches = [todaySearch, ...searches];
        hasChanges = true;
      } else {
        todaySearch.startDate = 'TODAY';
        todaySearch.endDate = 'TODAY';
        todaySearch.isDynamicToday = true;
        todaySearch.name = t('search.savedSearches.today', 'Heute');
        if (!Array.isArray(todaySearch.searchChips)) {
          todaySearch.searchChips = searchTextToChips(todaySearch.searchText || '');
        }
        if (!Array.isArray(todaySearch.conditions)) {
          todaySearch.conditions = [];
        }
      }
      
      // 2. "Diese Woche" Suche
      let thisWeekSearch = searches.find(s => s.id === thisWeekSearchId);
      if (!thisWeekSearch) {
        thisWeekSearch = {
          id: thisWeekSearchId,
          name: t('search.savedSearches.thisWeek', 'Diese Woche'),
          searchChips: [],
          conditions: [],
          searchText: '',
          startDate: 'THIS_WEEK',
          endDate: 'THIS_WEEK',
          isDynamicThisWeek: true
        };
        // Füge nach "Heute" ein
        const todayIndex = searches.findIndex(s => s.id === todaySearchId);
        if (todayIndex >= 0) {
          searches.splice(todayIndex + 1, 0, thisWeekSearch);
        } else {
          searches = [thisWeekSearch, ...searches];
        }
        hasChanges = true;
      } else {
        thisWeekSearch.startDate = 'THIS_WEEK';
        thisWeekSearch.endDate = 'THIS_WEEK';
        thisWeekSearch.isDynamicThisWeek = true;
        thisWeekSearch.name = t('search.savedSearches.thisWeek', 'Diese Woche');
        if (!Array.isArray(thisWeekSearch.searchChips)) {
          thisWeekSearch.searchChips = searchTextToChips(thisWeekSearch.searchText || '');
        }
        if (!Array.isArray(thisWeekSearch.conditions)) {
          thisWeekSearch.conditions = [];
        }
      }
      
      // 3. "Dieser Monat" Suche
      let thisMonthSearch = searches.find(s => s.id === thisMonthSearchId);
      if (!thisMonthSearch) {
        thisMonthSearch = {
          id: thisMonthSearchId,
          name: t('search.savedSearches.thisMonth', 'Dieser Monat'),
          searchChips: [],
          conditions: [],
          searchText: '',
          startDate: 'THIS_MONTH',
          endDate: 'THIS_MONTH',
          isDynamicThisMonth: true
        };
        // Füge nach "Diese Woche" ein
        const weekIndex = searches.findIndex(s => s.id === thisWeekSearchId);
        if (weekIndex >= 0) {
          searches.splice(weekIndex + 1, 0, thisMonthSearch);
        } else {
          searches = [thisMonthSearch, ...searches];
        }
        hasChanges = true;
      } else {
        thisMonthSearch.startDate = 'THIS_MONTH';
        thisMonthSearch.endDate = 'THIS_MONTH';
        thisMonthSearch.isDynamicThisMonth = true;
        thisMonthSearch.name = t('search.savedSearches.thisMonth', 'Dieser Monat');
        if (!Array.isArray(thisMonthSearch.searchChips)) {
          thisMonthSearch.searchChips = searchTextToChips(thisMonthSearch.searchText || '');
        }
        if (!Array.isArray(thisMonthSearch.conditions)) {
          thisMonthSearch.conditions = [];
        }
      }
      
      // Einstellungen speichern falls Änderungen
      const newSettings = {
        ...searchSettings,
        savedSearches: searches,
        defaultSearchId: hasChanges ? (searchSettings.defaultSearchId || todaySearchId) : searchSettings.defaultSearchId
      };
      
      saveSearchSettings(newSettings);
      setSavedSearches(searches);
      
      if (searchSettings.defaultSearchId !== undefined) {
        setDefaultSearchId(searchSettings.defaultSearchId);
      } else if (hasChanges) {
        setDefaultSearchId(todaySearchId);
      }
      
      // Default-Suche laden
      const defaultId = searchSettings.defaultSearchId || todaySearchId;
      const defaultSearch = searches.find(s => s.id === defaultId);
      
      if (defaultSearch) {
        // Suche laden - automatisch dynamische Datumsangaben behandeln
        const chips = Array.isArray(defaultSearch.searchChips)
          ? defaultSearch.searchChips
          : searchTextToChips(defaultSearch.searchText || '');
        setSearchChips(chips);
        loadConditions(defaultSearch.conditions || []);
        
        // Dynamische Datumsberechnung
        if (defaultSearch.isDynamicToday || defaultSearch.startDate === 'TODAY') {
          // Heute
          setStartDate(today);
          setEndDate(today);
        } else if (defaultSearch.isDynamicThisWeek || defaultSearch.startDate === 'THIS_WEEK') {
          // Diese Woche (Montag bis Sonntag)
          const now = new Date();
          const dayOfWeek = now.getDay(); // 0 = Sonntag, 1 = Montag, ...
          const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Montag als Wochenstart
          const monday = new Date(now);
          monday.setDate(now.getDate() + diff);
          const sunday = new Date(monday);
          sunday.setDate(monday.getDate() + 6);
          setStartDate(monday.toISOString().split('T')[0]);
          setEndDate(sunday.toISOString().split('T')[0]);
        } else if (defaultSearch.isDynamicThisMonth || defaultSearch.startDate === 'THIS_MONTH') {
          // Dieser Monat
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          setStartDate(firstDay.toISOString().split('T')[0]);
          setEndDate(lastDay.toISOString().split('T')[0]);
        } else {
          setStartDate(defaultSearch.startDate || '');
          setEndDate(defaultSearch.endDate || '');
        }
        
        setSelectedSearchId(defaultSearch.id);
      } else {
        // Fallback: Datum = Heute
        setSearchChips([]);
        clearConditions();
        setStartDate(today);
        setEndDate(today);
      }
    }
  }, [searchSettings, isInitialized, t]);

  // Spaltenbreiten-Änderungen speichern
  const handleColumnWidthChange = (params) => {
    const newWidths = {
      ...columnWidths,
      [params.colDef.field]: params.width
    };
    setColumnWidths(newWidths);
    
    const newSettings = {
      ...searchSettings,
      columnWidths: newWidths,
      columnVisibility: columnVisibilityModel,
      columnOrder: columnOrder,
      savedSearches: savedSearches,
      defaultSearchId: defaultSearchId
    };
    debounceSave(newSettings);
  };

  // Spaltensichtbarkeit-Änderungen speichern
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    
    const newSettings = {
      ...searchSettings,
      columnWidths: columnWidths,
      columnVisibility: newModel,
      columnOrder: columnOrder,
      savedSearches: savedSearches,
      defaultSearchId: defaultSearchId
    };
    debounceSave(newSettings);
  };
  
  // Spaltenreihenfolge-Änderungen speichern
  const handleColumnOrderSave = (newOrder) => {
    if (!Array.isArray(newOrder)) {
      console.error('handleColumnOrderSave: newOrder ist kein Array:', newOrder);
      return;
    }
    
    // Sortiere nach order property
    const sortedOrder = [...newOrder].sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : 0;
      const orderB = b.order !== undefined ? b.order : 0;
      return orderA - orderB;
    });
    
    // Extrahiere die Feldnamen
    const fieldOrder = sortedOrder.map(col => {
      if (typeof col === 'string') {
        return col;
      } else if (col && col.field) {
        return col.field;
      } else {
        console.warn('Ungültiges Spaltenobjekt:', col);
        return null;
      }
    }).filter(field => field !== null);
    
    setColumnOrder(fieldOrder);
    
    const newSettings = {
      ...searchSettings,
      columnWidths: columnWidths,
      columnVisibility: columnVisibilityModel,
      columnOrder: fieldOrder,
      savedSearches: savedSearches,
      defaultSearchId: defaultSearchId
    };
    
    saveSearchSettings(newSettings);
  };

  const handleColumnOrderChange = (fieldOrder) => {
    if (!Array.isArray(fieldOrder)) {
      console.error('handleColumnOrderChange: fieldOrder ist kein Array:', fieldOrder);
      return;
    }
    setColumnOrder(fieldOrder);
    const newSettings = {
      ...searchSettings,
      columnWidths: columnWidths,
      columnVisibility: columnVisibilityModel,
      columnOrder: fieldOrder,
      savedSearches: savedSearches,
      defaultSearchId: defaultSearchId
    };
    saveSearchSettings(newSettings);
  };

  const handleHeaderReorder = (fromField, toField) => {
    const current = (columnOrder.length > 0 ? columnOrder : SEARCH_RESULT_FIELDS).filter(Boolean);
    const next = reorderColumnFields(current, fromField, toField);
    if (next) {
      handleColumnOrderChange(next);
    }
  };
  
  // ========================================
  // Gespeicherte Suchen - Funktionen
  // ========================================
  
  // Aktuelle Suche speichern (aus Filter-Dialog)
  const handleSaveSearch = async ({ name, conditions: conditionsToSave, setAsDefault = true }) => {
    const filterName = String(name || '').trim();
    if (!filterName) {
      showError(t('search.saveSearch.nameRequired', 'Bitte geben Sie einen Namen ein'));
      return false;
    }
    
    // Prüfen ob Start- und Enddatum beide "heute" sind → dynamisch speichern
    const today = new Date().toISOString().split('T')[0];
    const isDynamicToday = startDate === today && endDate === today && startDate === endDate;
    
    const newSearch = {
      id: `search-${Date.now()}`,
      name: filterName,
      searchChips: searchChips,
      conditions: conditionsToSave || [],
      searchText: chipsToSearchText(searchChips),
      startDate: isDynamicToday ? 'TODAY' : startDate,
      endDate: isDynamicToday ? 'TODAY' : endDate,
      isDynamicToday: isDynamicToday
    };
    
    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    setSelectedSearchId(newSearch.id);
    const nextDefaultId = setAsDefault ? newSearch.id : defaultSearchId;
    if (setAsDefault) {
      setDefaultSearchId(newSearch.id);
    }
    
    const newSettings = {
      ...searchSettings,
      columnWidths: columnWidths,
      columnVisibility: columnVisibilityModel,
      columnOrder: columnOrder,
      savedSearches: updatedSearches,
      defaultSearchId: nextDefaultId
    };
    
    await saveSearchSettings(newSettings);
    showSuccess(t('search.saveSearch.saved', 'Suche gespeichert'));
    return true;
  };
  
  // Gespeicherte Suche laden
  const loadSearch = (search) => {
    const chips = Array.isArray(search.searchChips)
      ? search.searchChips
      : searchTextToChips(search.searchText || '');
    setSearchChips(chips);
    loadConditions(search.conditions || []);
    
    // Dynamische Datumsberechnung
    if (search.isDynamicToday || search.startDate === 'TODAY') {
      // Heute
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
    } else if (search.isDynamicThisWeek || search.startDate === 'THIS_WEEK') {
      // Diese Woche (Montag bis Sonntag)
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sonntag, 1 = Montag, ...
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Montag als Wochenstart
      const monday = new Date(now);
      monday.setDate(now.getDate() + diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      setStartDate(monday.toISOString().split('T')[0]);
      setEndDate(sunday.toISOString().split('T')[0]);
    } else if (search.isDynamicThisMonth || search.startDate === 'THIS_MONTH') {
      // Dieser Monat
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    } else {
      setStartDate(search.startDate || '');
      setEndDate(search.endDate || '');
    }
    
    setSelectedSearchId(search.id);
  };
  
  // Suche als Standard setzen/entfernen
  const toggleDefaultSearch = (searchId) => {
    const newDefaultId = defaultSearchId === searchId ? null : searchId;
    setDefaultSearchId(newDefaultId);
    
    const newSettings = {
      ...searchSettings,
      columnWidths: columnWidths,
      columnVisibility: columnVisibilityModel,
      columnOrder: columnOrder,
      savedSearches: savedSearches,
      defaultSearchId: newDefaultId
    };
    
    saveSearchSettings(newSettings);
  };
  
  // Gespeicherte Suche auswählen
  const handleSearchSelect = (event) => {
    const searchId = event.target.value;
    
    if (!searchId) {
      // "Keine Auswahl" - zurücksetzen
      setSearchChips([]);
      clearConditions();
      setStartDate('');
      setEndDate('');
      setSelectedSearchId('');
      return;
    }
    
    const search = savedSearches.find(s => s.id === searchId);
    if (search) {
      loadSearch(search);
    }
  };
  
  // Gespeicherte Suche löschen
  const handleDeleteSearch = (searchId) => {
    // "Heute" Suche darf nicht gelöscht werden
    if (searchId === 'search-today') {
      showError(t('search.savedSearches.cannotDeleteToday', '"Heute" kann nicht gelöscht werden'));
      return;
    }
    
    const updatedSearches = savedSearches.filter(s => s.id !== searchId);
    setSavedSearches(updatedSearches);
    
    // Wenn die gelöschte Suche der Default war, auf "Heute" zurücksetzen
    let newDefaultId = defaultSearchId;
    if (defaultSearchId === searchId) {
      newDefaultId = 'search-today';
      setDefaultSearchId(newDefaultId);
    }
    
    // Wenn die gelöschte Suche aktuell ausgewählt war, zurücksetzen
    if (selectedSearchId === searchId) {
      setSelectedSearchId('');
      setSearchChips([]);
      clearConditions();
      setStartDate('');
      setEndDate('');
    }
    
    const newSettings = {
      ...searchSettings,
      columnWidths: columnWidths,
      columnVisibility: columnVisibilityModel,
      columnOrder: columnOrder,
      savedSearches: updatedSearches,
      defaultSearchId: newDefaultId
    };
    
    saveSearchSettings(newSettings);
  };

  // Hilfsfunktion für Status-zu-CSS-Klasse Mapping - SPRACHUNABHÄNGIG
  const getStatusCssClass = (status) => {
    if (!status) return 'status-new';
    
    // Normalisierung: Case-insensitive Matching
    const normalizedStatus = status.toString().toLowerCase().trim();
    
    // AUSSCHLIESSLICH Backend ReportStatus Enum Werte - CSS-Klassen entsprechen exakt Backend-Namen
    const statusMap = {
      // Diese Werte stehen in der Datenbank (Backend Enum):
      'new': 'status-new',
      'started': 'status-started',
      'provisionally': 'status-provisionally',
      'awaiting_approval': 'status-awaiting-approval',
      'approved': 'status-approved',
      'signed': 'status-approved', // SIGNED wird wie APPROVED angezeigt
      'rejected': 'status-rejected'
    };
    
    return statusMap[normalizedStatus] || 'status-new';
  };

  // Status Label
  const getStatusLabel = (status) => {
    return t(`reportStatus.${status}`, status);
  };
  
  // ========================================
  // PDF Preview bei Doppelklick
  // ========================================
  
  const handleRowDoubleClick = async (params) => {
    console.log('🖱️ Doppelklick auf Zeile:', params.row);
    
    // Prüfe ob es sich um einen Report oder eine Exam handelt
    const reportId = params.row.report_id;
    
    if (!reportId) {
      showError(t('search.noReportAvailable', 'Für diese Untersuchung existiert noch kein Befund'));
      return;
    }
    
    const terms = extractSearchTerms(chipsToSearchText(searchChips));
    console.log('🔍🔍🔍 Setting highlightTerms to:', terms);
    setHighlightTerms(terms);
    
    try {
      console.log('📥 Lade PDF-Daten für Report:', reportId);
      
      // PDF-Daten vom Backend laden
      const response = await apiFetch(`/reports/${reportId}/pdf`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ PDF-Daten erhalten:', result);
        console.log('🔍 POPUP BACKEND RESPONSE DEBUG - result.pdf_data:', result.pdf_data);
        console.log('🔍 POPUP BACKEND RESPONSE DEBUG - Template-Pfad:', result.pdf_data?.report?.report?.metadata?.template_type);
        
        // Öffne PDF-Vorschau in separatem Fenster
        if (result.pdf_data) {
          // ✅ NEUE LÖSUNG: Template-Typ-bewusste Popup-Öffnung
          console.log('🪟 === POPUP TEMPLATE TYPE DETECTION ===');
          const templateType = result.pdf_data?.report?.report?.metadata?.template_type || 
                              result.pdf_data?.template?.template_type || 'F';
          console.log('🪟 Template Type für Popup:', templateType);
          
          // 🚀 BEIDE Template-Typen verwenden Popup-Fenster
          console.log('🪟 📋📝 Öffne Popup-Fenster für Template-Typ:', templateType);
          
          // Speichere PDF-Daten temporär in localStorage für neues Fenster
          // WICHTIG: Template-Typ in den Key einbetten für Popup-System
          // WICHTIG: Füge highlightTerms zu den PDF-Daten hinzu
          const pdfDataWithHighlight = {
            ...result.pdf_data,
            highlightTerms: terms
          };
          console.log('🔍🔍🔍 Adding highlightTerms to localStorage:', terms);
          
          const tempKey = `pdf_preview_${reportId}_${templateType}_${Date.now()}`;
          localStorage.setItem(tempKey, JSON.stringify(pdfDataWithHighlight));
          
          // Öffne neues Fenster mit PDF Preview (mit Template-Typ Information)
          // Optimale Größe für A4-Darstellung (210mm @ 96dpi ≈ 794px + UI)
          const width = 950; // An A4-Seitenbreite angepasst
          const height = Math.floor(window.screen.availHeight * 0.85); // Nicht ganz so hoch
          // Positioniere in der Mitte der linken Bildschirmhälfte
          const left = Math.floor((window.screen.availWidth / 2 - width) / 2);
          const top = Math.floor((window.screen.availHeight - height) / 2);
          
          const newWindow = window.open(
            `/pdf-preview?key=${tempKey}`,
            `pdf_preview_${reportId}`,
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
          );
          
          if (!newWindow) {
            showError(t('search.popupBlocked', 'Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.'));
            localStorage.removeItem(tempKey);
            // Fallback: Öffne im aktuellen Tab mit Template-Typ-Erkennung
            openPdfPreview(result.pdf_data);
          } else {
            // Auto-cleanup nach 10 Minuten
            setTimeout(() => {
              try {
                localStorage.removeItem(tempKey);
              } catch (e) {
                console.warn('Cleanup-Fehler für PDF Preview Key:', e);
              }
            }, 10 * 60 * 1000);
          }
        } else {
          throw new Error('Keine PDF-Daten vom Backend erhalten');
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unbekannter Fehler' }));
        throw new Error(errorData.detail || 'Fehler beim Laden der PDF-Daten');
      }

    } catch (error) {
      console.error('❌ Fehler beim Laden der PDF-Daten:', error);
      showError('Fehler beim Laden der PDF-Vorschau: ' + error.message);
    }
  };

  // Ref für DataGrid Container
  const dataGridRef = useRef(null);

  // Kontextmenü mit direktem Event Listener
  useEffect(() => {
    const handleContextMenu = (event) => {
      // Prüfe ob das Event von einer DataGrid-Zeile kommt
      const row = event.target.closest('.MuiDataGrid-row');
      
      if (row) {
        event.preventDefault();
        event.stopPropagation();
        
        // Extrahiere die Row-ID aus dem data-id Attribut
        const rowId = row.getAttribute('data-id');
        console.log('🖱️ Kontextmenü für Row-ID:', rowId);
        
        // Finde die zugehörige Zeile in den Ergebnissen
        // WICHTIG: getRowId verwendet jetzt `exam-${exam_id}` für alle Zeilen
        const rowData = results.find(r => {
          const id = `exam-${r.exam_id}`;
          return String(id) === String(rowId);
        });
        
        if (rowData) {
          console.log('🖱️ Row-Daten gefunden:', rowData);
    console.log('🔍 DEBUG Row-Daten Details:', {
      report_id: rowData.report_id,
      study_instance_uid: rowData.study_instance_uid,
      exam_study_instance_uid: rowData.exam_study_instance_uid,
      new_report_id: rowData.new_report_id,
      exam_id: rowData.exam_id
    });
          setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            row: rowData
          });
        }
      }
    };

    const gridElement = dataGridRef.current;
    if (gridElement) {
      gridElement.addEventListener('contextmenu', handleContextMenu);
      return () => {
        gridElement.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, [results]);

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setAiSubMenuAnchor(null);
  };

  const handleAiSubMenuOpen = (event) => {
    setAiSubMenuAnchor(event.currentTarget);
  };

  const handleAiSubMenuClose = () => {
    setAiSubMenuAnchor(null);
  };

  const handleOpenAiSummaryPopup = (promptName, customPrompt, scope) => {
    if (!contextMenu?.row?.patient_id) return;
    const patientId = contextMenu.row.patient_id;
    const examId = contextMenu.row.exam_id;

    const width = 550;
    const height = 700;
    const left = Math.floor((window.screen.availWidth - width) / 2);
    const top = Math.floor((window.screen.availHeight - height) / 2);

    const urlParams = new URLSearchParams();
    urlParams.set('patientId', patientId);
    if (promptName) urlParams.set('promptName', promptName);
    if (examId && scope === 'current_exam') urlParams.set('examId', examId);

    if (customPrompt) {
      const storageKey = `ai_summary_prompt_${Date.now()}`;
      localStorage.setItem(storageKey, customPrompt);
      urlParams.set('storageKey', storageKey);
    }

    const isDark = document.documentElement.getAttribute('data-mui-color-scheme') === 'dark'
      || document.body.classList.contains('dark-mode')
      || window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    if (isDark) urlParams.set('dark', '1');

    window.open(
      `/ai-summary-popup?${urlParams.toString()}`,
      `ai_summary_${patientId}_${promptName || 'system'}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
    );

    handleContextMenuClose();
  };

  // PDF Preview öffnen mit Template-Typ-Routing (wie im ReportEditor)
  const openPdfPreview = (pdfData) => {
    console.log('📄 === PDF PREVIEW DEBUG START ===');
    console.log('📄 Received pdfData:', pdfData);
    console.log('📄 pdfData Structure:', JSON.stringify(pdfData, null, 2));
    
    // Template-Typ ermitteln - mit detailliertem Debugging
    const templateType = pdfData?.report?.report?.metadata?.template_type || 'F';
    
    console.log('🔍 === TEMPLATE TYPE DETECTION ===');
    console.log('🔍 pdfData.report:', pdfData?.report);
    console.log('🔍 pdfData.report.report:', pdfData?.report?.report);
    console.log('🔍 pdfData.report.report.metadata:', pdfData?.report?.report?.metadata);
    console.log('🔍 Raw template_type value:', pdfData?.report?.report?.metadata?.template_type);
    console.log('🔍 Final templateType (with fallback):', templateType);
    
    // Extrahiere highlightTerms aus pdfData, falls vorhanden
    if (pdfData.highlightTerms) {
      console.log('🔍🔍🔍 Extracting highlightTerms from pdfData:', pdfData.highlightTerms);
      setHighlightTerms(pdfData.highlightTerms);
    }
    
    setPdfData(pdfData);
    
    if (templateType === 'S') {
      // Strukturierter Befund
      console.log('📋 ✅ OPENING STRUCTURED PDF DIALOG');
      console.log('📋 setPdfPreviewStructuredOpen(true) wird aufgerufen');
      setPdfPreviewStructuredOpen(true);
    } else {
      // Freitext-Befund (Standard)
      console.log('📝 ✅ OPENING FREETEXT PDF DIALOG');
      console.log('📝 setPdfPreviewOpen(true) wird aufgerufen');
      console.log('📝 Template type was:', templateType, '(F=Freitext, S=Strukturiert)');
      setPdfPreviewOpen(true);
    }
    
    console.log('📄 === PDF PREVIEW DEBUG END ===');
  };

  const handlePrintFromContext = async () => {
    if (!contextMenu || !contextMenu.row) return;
    
    const reportId = contextMenu.row.report_id;
    handleContextMenuClose();
    
    if (!reportId) {
      showError(t('search.noReportAvailable', 'Für diese Untersuchung existiert noch kein Befund'));
      return;
    }
    
    const terms = extractSearchTerms(chipsToSearchText(searchChips));
    console.log('🔍🔍🔍 Setting highlightTerms to (print):', terms);
    setHighlightTerms(terms);
    
    try {
      console.log('🖨️ Lade PDF-Daten für Drucken:', reportId);
      
      // PDF-Daten vom Backend laden
      const response = await apiFetch(`/reports/${reportId}/pdf`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ PDF-Daten erhalten für Drucken:', result);
        console.log('🔍 BACKEND RESPONSE DEBUG - result.pdf_data:', result.pdf_data);
        console.log('🔍 BACKEND RESPONSE DEBUG - Template-Pfad:', result.pdf_data?.report?.report?.metadata?.template_type);
        
        if (result.pdf_data) {
          // Markiere für Auto-Download (= Drucken) und füge highlightTerms hinzu
          result.pdf_data.autoDownload = true;
          result.pdf_data.highlightTerms = terms;
          console.log('🔍🔍🔍 Adding highlightTerms to pdfData for print:', terms);
          openPdfPreview(result.pdf_data);
        } else {
          throw new Error('Keine PDF-Daten vom Backend erhalten');
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unbekannter Fehler' }));
        throw new Error(errorData.detail || 'Fehler beim Laden der PDF-Daten');
      }

    } catch (error) {
      console.error('❌ Fehler beim Laden der PDF-Daten:', error);
      showError('Fehler beim PDF-Druck: ' + error.message);
    }
  };

  const handleOpenViewer = async () => {
    if (!contextMenu || !contextMenu.row) return;
    
    const row = contextMenu.row;
    handleContextMenuClose();
    
    // StudyInstanceUID aus verschiedenen Quellen extrahieren
    let studyInstanceUID = null;
    
    console.log('🔍 DEBUG Viewer: Extrahiere StudyInstanceUID aus Row-Daten:', {
      study_instance_uid: row.study_instance_uid,
      exam_study_instance_uid: row.exam_study_instance_uid,
      new_report_id: row.new_report_id,
      exam_id: row.exam_id
    });
    
    if (row.study_instance_uid) {
      studyInstanceUID = row.study_instance_uid;
      console.log('✅ StudyInstanceUID gefunden in row.study_instance_uid:', studyInstanceUID);
    } else if (row.exam_study_instance_uid) {
      studyInstanceUID = row.exam_study_instance_uid;
      console.log('✅ StudyInstanceUID gefunden in row.exam_study_instance_uid:', studyInstanceUID);
    }
    
    if (!studyInstanceUID) {
      console.log('❌ Keine StudyInstanceUID gefunden in Row-Daten');
      showError(t('search.viewer.noStudyUID', 'ERR-VIEWER-001: StudyInstanceUID nicht verfügbar'));
      return;
    }
    
    if (!currentUser || !currentUser.viewer_preference) {
      console.log('❌ DEBUG Viewer: User/Preference Problem:', {
        currentUser: currentUser,
        hasUser: !!currentUser,
        viewerPreference: currentUser?.viewer_preference,
        viewerConfig: viewerConfig
      });
      
      // Fallback: Versuche User-Daten nochmal zu laden wenn Auth verfügbar ist
      const authHeaders = getAuthHeaders();
      
      if (authHeaders.Authorization && !currentUser) {
        console.log('🔄 DEBUG: Fallback - Lade User-Daten vor Viewer-Öffnung...');
        try {
          await loadViewerData();
          // Rekursiver Aufruf nach dem Laden
          setTimeout(() => handleOpenViewer(), 100);
          return;
        } catch (error) {
          console.error('❌ Fallback User-Daten laden fehlgeschlagen:', error);
        }
      }
      
      showError(t('search.viewer.noPreference', 'ERR-VIEWER-002: Viewer-Einstellung nicht verfügbar'));
      return;
    }
    
    // Modalität für spezielle Viewer-Logik ermitteln
    const studyModality = row.modality ? row.modality.toUpperCase() : '';
    
    let userViewerType;
    let forceWebViewer = false;
    
    // Spezielle Modalitäten immer mit Webviewer öffnen
    if (studyModality.includes('ECG') || studyModality.includes('EKG')) {
      forceWebViewer = true;
    } else if (studyModality === 'OT' || studyModality === 'DOC') {
      forceWebViewer = true;
    }
    
    if (forceWebViewer) {
      userViewerType = 'web';
    } else {
      userViewerType = currentUser.viewer_preference;
    }
    
    const viewerSetting = viewerConfig[userViewerType];
    
    if (!viewerSetting) {
      if (forceWebViewer) {
        showError(t('search.viewer.webViewerNotConfigured', {
          defaultValue: 'ERR-VIEWER-003: Webviewer für Modalität "{{modality}}" ist nicht konfiguriert',
          modality: studyModality
        }));
      } else {
        showError(t('search.viewer.viewerNotConfigured', {
          defaultValue: 'ERR-VIEWER-004: Viewer "{{viewerType}}" ist nicht konfiguriert',
          viewerType: userViewerType
        }));
      }
      return;
    }
    
    let baseUrl = viewerSetting.base_url;
    
    if (!baseUrl) {
      showError(t('search.viewer.noViewerURL', {
        defaultValue: 'ERR-VIEWER-005: Keine Viewer-URL für "{{viewerType}}" konfiguriert',
        viewerType: userViewerType
      }));
      return;
    }
    
    // StudyInstanceUID in URL einsetzen
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', studyInstanceUID);
    
    try {
      if (userViewerType === 'web') {
        openInViewerWindow(viewerUrl);
        showSuccess(t('search.viewer.opened', 'Viewer geöffnet.'));
      } else if (userViewerType === 'client') {
        // Client-Anwendung starten
        window.location.href = viewerUrl;
        showSuccess(t('search.viewer.opened', 'Viewer geöffnet.'));
      }
    } catch (error) {
      console.error('Error opening viewer:', error);
      showError(t('search.viewer.openError', 'ERR-VIEWER-006: Fehler beim Öffnen des Viewers'));
    }
  };

  const handleAddendumFromContext = () => {
    if (!contextMenu || !contextMenu.row) return;
    
    const reportId = contextMenu.row.report_id;
    const examId = contextMenu.row.exam_id;
    const isCombined = contextMenu.row.is_combined || contextMenu.row.grouped_report_id;
    
    if (!reportId || !examId) {
      showError(t('search.error.noReportId', 'Kein Befund ausgewählt'));
      handleContextMenuClose();
      return;
    }
    
    setAddendumReportId(reportId);
    setAddendumExamId(examId);
    // Speichere ob es ein kombinierter Befund ist
    setAddendumIsCombined(!!isCombined);
    setAddendumDialogOpen(true);
    handleContextMenuClose();
  };
  
  const handleAddendumConfirm = async () => {
    if (!addendumReportId) return;
    
    try {
      // API-Call zum Addendum erstellen
      const response = await apiFetch(`reports/${addendumReportId}/addendum`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to create addendum');
      }
      
      const data = await response.json();
      
      setAddendumDialogOpen(false);
      
      showSuccess(t('search.addendum.success', 'Addendum wird vorbereitet...'));
      
      // Öffne ReportEditor - für kombinierte Befunde die combined-Route verwenden
      if (addendumIsCombined && addendumReportId) {
        // Kombinierter Befund: Öffne mit reportId
        navigate(`/report/combined/${addendumReportId}?addendum=true`);
      } else if (addendumExamId) {
        // Einzelner Befund: Öffne mit examId
        navigate(`/report/${addendumExamId}?addendum=true`);
      }
      
      // States zurücksetzen
      setAddendumReportId(null);
      setAddendumExamId(null);
      setAddendumIsCombined(false);
    } catch (error) {
      console.error('Fehler beim Addendum:', error);
      showError(t('search.addendum.error', 'Fehler beim Erstellen des Addendums'));
    }
  };
  
  const handleAddendumCancel = () => {
    setAddendumDialogOpen(false);
    setAddendumReportId(null);
    setAddendumExamId(null);
    setAddendumIsCombined(false);
  };

  const handleHl7ResendFromContext = async () => {
    if (!contextMenu || !contextMenu.row) return;
    
    const reportId = contextMenu.row.report_id;
    handleContextMenuClose();
    
    if (!reportId) {
      showError(t('search.noReportAvailable', 'Für diese Untersuchung existiert noch kein Befund'));
      return;
    }

    try {
      setLoading(true);
      
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      };
      
      if (!headers.Authorization) {
        showError(t('search.hl7ResendError', {
          defaultValue: 'ERR-HL7-001: HL7 Resend fehlgeschlagen: {{error}}',
          error: 'Keine Authentifizierung verfügbar'
        }));
        return;
      }
      
      // HL7 Resend API-Aufruf
      const response = await fetch(`${getApiBaseUrl()}/reports/${reportId}/resend-hl7`, {
        method: 'POST',
        headers: headers,
      });
      
      if (response.ok) {
        const result = await response.json();
        showSuccess(t('search.hl7ResendSuccess', {
          defaultValue: 'HL7 Befund erfolgreich an {{targetNode}} gesendet',
          targetNode: result.target_node || 'Zielknoten'
        }));
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `HTTP ${response.status}`;
        showError(t('search.hl7ResendError', {
          defaultValue: 'ERR-HL7-002: HL7 Resend fehlgeschlagen: {{error}}',
          error: errorMessage
        }));
      }
    } catch (error) {
      console.error('Error during HL7 resend:', error);
      showError(t('search.hl7ResendError', {
        defaultValue: 'ERR-HL7-002: HL7 Resend fehlgeschlagen: {{error}}',
        error: error.message
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleResendFromContext = async () => {
    if (!contextMenu || !contextMenu.row) return;
    
    const reportId = contextMenu.row.report_id;
    handleContextMenuClose();
    
    if (!reportId) {
      showError(t('search.noReportAvailable', 'Für diese Untersuchung existiert noch kein Befund'));
      return;
    }

    try {
      setLoading(true);
      
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      };
      
      if (!headers.Authorization) {
        showError(t('search.dicomResendError', {
          defaultValue: 'ERR-DICOM-001: DICOM Resend fehlgeschlagen: {{error}}',
          error: 'Keine Authentifizierung verfügbar'
        }));
        return;
      }
      
      // Aktuelle UI-Sprache und SeriesDescription ermitteln
      const currentLanguage = i18n.language || 'de';
      const language = currentLanguage.split('-')[0]; // 'de-DE' -> 'de'
      const seriesDescription = t('search.dicom.seriesDescription', 'Befund');
      
      const response = await fetch(`${API_BASE}/reports/${reportId}/dicom-resend`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          language: language,
          seriesDescription: seriesDescription
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'DICOM Resend fehlgeschlagen');
      }

      const result = await response.json();
      
      showSuccess(t('search.dicomResendSuccess', {
        defaultValue: 'DICOM PDF erfolgreich an {{targetNode}} gesendet (Instance: {{instanceNumber}}/100)',
        targetNode: result.target_node,
        instanceNumber: result.instance_number
      }));
      
    } catch (error) {
      console.error('❌ DICOM Resend Fehler:', error);
      showError(t('search.dicomResendError', {
        defaultValue: 'ERR-DICOM-002: DICOM Resend fehlgeschlagen: {{error}}',
        error: error.message
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleDicomPdfFromContext = () => {
    if (!contextMenu || !contextMenu.row) return;
    
    const reportId = contextMenu.row.report_id;
    handleContextMenuClose();
    
    if (!reportId) {
      showError(t('search.noReportAvailable', 'Für diese Untersuchung existiert noch kein Befund'));
      return;
    }
    
    // DICOM PDF Viewer öffnen
    setDicomPdfReportId(reportId);
    setDicomPdfOpen(true);
  };
  
  // ========================================
  // CSV-Export
  // ========================================
  
  const handleExportCSV = () => {
    try {
      // Erstelle Dateinamen mit aktuellem Datum und Zeit
      const now = new Date();
      const dateStr = now.toLocaleDateString('de-DE').replace(/\./g, '-');
      const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }).replace(':', '-');
      const filename = `Search_${dateStr}_${timeStr}.csv`;
      
      // Erstelle Suchparameter-String für erste Zeile
      let searchParams = `Suche: ${chipsToSearchText(searchChips) || '(alle)'}`;
      if (startDate) {
        searchParams += `; Von: ${startDate}`;
      }
      if (endDate) {
        searchParams += `; Bis: ${endDate}`;
      }
      
      // Hole sichtbare Spalten in der richtigen Reihenfolge
      const orderedFields = columnOrder.length > 0 ? columnOrder : defaultColumnOrder;
      const visibleColumns = orderedFields
        .filter(field => columnVisibilityModel[field] !== false)
        .map(field => allColumns.find(col => col.field === field))
        .filter(col => col);
      
      // Erstelle CSV-Header (Spaltenüberschriften)
      const headers = visibleColumns.map(col => {
        const headerName = col.headerName || col.field;
        // Escape Anführungszeichen in Header
        return `"${headerName.replace(/"/g, '""')}"`;
      }).join(';');
      
      // Erstelle CSV-Zeilen mit Daten
      const rows = filteredResults.map(row => {
        return visibleColumns.map(col => {
          let value = row[col.field];
          
          // Formatiere spezielle Felder
          if (col.field === 'report_status') {
            value = t(`reportStatus.${value}`, value);
          } else if (col.field === 'birth_date' && value) {
            value = formatDateWithAgeSync(value);
          } else if (['observation_date', 'approval_time', 'report_approved_at', 'created_at'].includes(col.field) && value) {
            value = formatDateSync(value);
          } else if (value === null || value === undefined) {
            value = '';
          }
          
          // Konvertiere zu String und escape Anführungszeichen
          const strValue = String(value).replace(/"/g, '""');
          return `"${strValue}"`;
        }).join(';');
      }).join('\n');
      
      // Kombiniere alles: Suchparameter, Header, Daten
      const csv = `"${searchParams.replace(/"/g, '""')}"\n${headers}\n${rows}`;
      
      // Erstelle Blob und Download
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(t('search.exportSuccess', 'CSV-Export erfolgreich'));
    } catch (error) {
      console.error('Fehler beim CSV-Export:', error);
      showError(t('search.exportError', 'Fehler beim CSV-Export'));
    }
  };

  // Verfügbare Spalten-Definitionen
  const allColumns = [
    {
      field: 'report_status',
      headerName: t('search.columns.reportStatus', 'Befundstatus'),
      width: columnWidths['report_status'] || 150,
      renderCell: (params) => {
        // Wenn kein Report vorhanden ist, zeige nichts
        if (!params.value || params.value === '') {
          return null;
        }
        const statusCssClass = getStatusCssClass(params.value);
        
        // Prüfe ob Addendum vorhanden ist
        let hasAddendum = false;
        try {
          if (params.row.addendum) {
            if (typeof params.row.addendum === 'string') {
              const parsed = JSON.parse(params.row.addendum);
              hasAddendum = Array.isArray(parsed) && parsed.length > 0;
            } else if (Array.isArray(params.row.addendum)) {
              hasAddendum = params.row.addendum.length > 0;
            }
          }
          
          // Debug-Log für APPROVED-Befunde
          if (params.value === 'APPROVED') {
            console.log('🔍 APPROVED Badge Debug:', {
              exam_id: params.row.exam_id,
              report_id: params.row.report_id,
              addendum_raw: params.row.addendum,
              addendum_type: typeof params.row.addendum,
              hasAddendum: hasAddendum
            });
          }
        } catch (e) {
          console.error('Error parsing addendum:', e, params.row.addendum);
        }
        
        return (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Chip 
              label={getStatusLabel(params.value)} 
              size="small"
              className={`chip-Befundstatus ${statusCssClass}`}
              sx={{
                fontWeight: 500,
                fontSize: '0.8125rem'
              }}
            />
            {hasAddendum && params.value === 'APPROVED' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  border: '2px solid white',
                  boxShadow: 1
                }}
              >
                A
              </Box>
            )}
          </Box>
        );
      }
    },
    {
      field: 'exam_status',
      headerName: t('search.columns.examStatus', 'Untersuchungsstatus'),
      width: columnWidths['exam_status'] || 150,
      renderCell: (params) => {
        if (!params.value || params.value === '') {
          return null;
        }
        const statusCssClass = getStatusCssClass(params.value);
        return (
          <Chip 
            label={getStatusLabel(params.value)} 
            size="small"
            className={`chip-Befundstatus ${statusCssClass}`}
            sx={{
              fontWeight: 500,
              fontSize: '0.8125rem'
            }}
          />
        );
      }
    },
    {
      field: 'patient_name',
      headerName: t('search.columns.patientName', 'Patientenname'),
      width: columnWidths['patient_name'] || 240  // 20% breiter (200 -> 240)
    },
    {
      field: 'birth_date',
      headerName: t('search.columns.birthDate', 'Geburtsdatum'),
      width: columnWidths['birth_date'] || 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        
        // Warte kurz auf Format-Config, falls noch nicht geladen
        if (!isDateFormatLoaded) {
          return params.value; // Zeige ISO-Format temporär
        }
        
        // Berechne Datum und Alter separat
        const birthDate = new Date(params.value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        const formattedDate = formatDateSync(params.value);
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
            <span>{formattedDate}</span>
            <span style={{ fontSize: '0.85em', color: 'var(--rd-text-secondary)' }}>({age} Jahre)</span>
          </div>
        );
      }
    },
    {
      field: 'patient_id',
      headerName: t('search.columns.patientId', 'Patienten-ID'),
      width: columnWidths['patient_id'] || 120
    },
    {
      field: 'accession_number',
      headerName: t('search.columns.accessionNumber', 'Accession Number'),
      width: columnWidths['accession_number'] || 150
    },
    {
      field: 'modality',
      headerName: t('search.columns.modality', 'Modalität'),
      width: columnWidths['modality'] || 100
    },
    {
      field: 'study_description',
      headerName: t('search.columns.studyDescription', 'Untersuchung'),
      width: columnWidths['study_description'] || 125  // Halb so groß (250 -> 125)
    },
    {
      field: 'approved_by',
      headerName: t('search.columns.approvedBy', 'Freigegeben von'),
      width: columnWidths['approved_by'] || 180
    },
    {
      field: 'approval_time',
      headerName: t('search.columns.approvalTime', 'Freigegeben am'),
      width: columnWidths['approval_time'] || 150,
      renderCell: (params) => params.value ? formatDateTimeSync(params.value) : '-'
    },
    {
      field: 'signed_by_names',
      headerName: t('search.columns.signedBy', 'Befunder'),
      width: columnWidths['signed_by_names'] || 180
    },
    {
      field: 'report_approved_at',
      headerName: t('search.columns.reportApprovedAt', 'Befunddatum'),
      width: columnWidths['report_approved_at'] || 150,
      renderCell: (params) => params.value ? formatDateTimeSync(params.value) : '-'
    },
    {
      field: 'comment',
      headerName: t('search.columns.comment', 'Kommentar'),
      width: columnWidths['comment'] || 200
    },
    {
      field: 'scan_protocol',
      headerName: t('search.columns.scanProtocol', 'Scan Protokoll'),
      width: columnWidths['scan_protocol'] || 150
    }
  ];

  // Standard-Spaltenreihenfolge
  const defaultColumnOrder = SEARCH_RESULT_FIELDS;

  // DataGrid-Spalten mit Reihenfolge und Sichtbarkeit
  const dataGridColumns = useMemo(() => {
    const rawFields = columnOrder.length > 0 ? columnOrder : defaultColumnOrder;
    const seenFields = new Set();
    const orderedFields = rawFields.filter((field) => {
      if (!field || seenFields.has(field)) return false;
      seenFields.add(field);
      return true;
    });
    
    return orderedFields
      .map(field => allColumns.find(col => col.field === field))
      .filter(col => col && columnVisibilityModel[col.field] !== false);
  }, [allColumns, columnOrder, columnVisibilityModel, columnWidths, t]);

  const dataGridColumnsWithDnd = useMemo(() => (
    dataGridColumns.map((col) => ({
      ...col,
      renderHeader: (params) => (
        <DraggableColumnHeader field={params.field} onReorder={handleHeaderReorder}>
          {params.colDef.headerName}
        </DraggableColumnHeader>
      )
    }))
  ), [dataGridColumns]);

  // Zeile anklicken -> nur selektieren (Doppelklick kommt später)

  // Deutsche Übersetzungen für DataGrid
  const deLocaleText = {
    ...GRID_DEFAULT_LOCALE_TEXT,
    noRowsLabel: 'Keine Ergebnisse',
    // Spaltenmenü
    columnMenuLabel: 'Menü',
    columnMenuShowColumns: 'Spalten anzeigen',
    columnMenuFilter: 'Filter',
    columnMenuHideColumn: 'Spalte ausblenden',
    columnMenuUnsort: 'Sortierung aufheben',
    columnMenuSortAsc: 'Aufsteigend sortieren',
    columnMenuSortDesc: 'Absteigend sortieren',
    columnMenuManageColumns: 'Spalten verwalten',
    // Filter
    filterPanelAddFilter: 'Filter hinzufügen',
    filterPanelDeleteIconLabel: 'Löschen',
    filterPanelOperators: 'Operatoren',
    filterPanelOperatorAnd: 'Und',
    filterPanelOperatorOr: 'Oder',
    filterPanelColumns: 'Spalten',
    filterPanelInputLabel: 'Wert',
    filterPanelInputPlaceholder: 'Filterwert',
    filterOperatorContains: 'enthält',
    filterOperatorEquals: 'ist gleich',
    filterOperatorStartsWith: 'beginnt mit',
    filterOperatorEndsWith: 'endet mit',
    filterOperatorIsEmpty: 'ist leer',
    filterOperatorIsNotEmpty: 'ist nicht leer',
    filterOperatorIsAnyOf: 'ist einer von',
  };

  // Französische Übersetzungen für DataGrid
  const frLocaleText = {
    ...GRID_DEFAULT_LOCALE_TEXT,
    noRowsLabel: 'Aucun résultat',
    // Menu des colonnes
    columnMenuLabel: 'Menu',
    columnMenuShowColumns: 'Afficher les colonnes',
    columnMenuFilter: 'Filtrer',
    columnMenuHideColumn: 'Masquer la colonne',
    columnMenuUnsort: 'Annuler le tri',
    columnMenuSortAsc: 'Trier par ordre croissant',
    columnMenuSortDesc: 'Trier par ordre décroissant',
    columnMenuManageColumns: 'Gérer les colonnes',
    // Filtre
    filterPanelAddFilter: 'Ajouter un filtre',
    filterPanelDeleteIconLabel: 'Supprimer',
    filterPanelOperators: 'Opérateurs',
    filterPanelOperatorAnd: 'Et',
    filterPanelOperatorOr: 'Ou',
    filterPanelColumns: 'Colonnes',
    filterPanelInputLabel: 'Valeur',
    filterPanelInputPlaceholder: 'Valeur du filtre',
    filterOperatorContains: 'contient',
    filterOperatorEquals: 'égal à',
    filterOperatorStartsWith: 'commence par',
    filterOperatorEndsWith: 'se termine par',
    filterOperatorIsEmpty: 'est vide',
    filterOperatorIsNotEmpty: "n'est pas vide",
    filterOperatorIsAnyOf: 'est l\'un de',
  };

  // Englische Übersetzungen für DataGrid
  const enLocaleText = {
    ...GRID_DEFAULT_LOCALE_TEXT,
    noRowsLabel: 'No results',
    // Column menu
    columnMenuLabel: 'Menu',
    columnMenuShowColumns: 'Show columns',
    columnMenuFilter: 'Filter',
    columnMenuHideColumn: 'Hide column',
    columnMenuUnsort: 'Unsort',
    columnMenuSortAsc: 'Sort by ASC',
    columnMenuSortDesc: 'Sort by DESC',
    columnMenuManageColumns: 'Manage columns',
    // Filter
    filterPanelAddFilter: 'Add filter',
    filterPanelDeleteIconLabel: 'Delete',
    filterPanelOperators: 'Operators',
    filterPanelOperatorAnd: 'And',
    filterPanelOperatorOr: 'Or',
    filterPanelColumns: 'Columns',
    filterPanelInputLabel: 'Value',
    filterPanelInputPlaceholder: 'Filter value',
    filterOperatorContains: 'contains',
    filterOperatorEquals: 'equals',
    filterOperatorStartsWith: 'starts with',
    filterOperatorEndsWith: 'ends with',
    filterOperatorIsEmpty: 'is empty',
    filterOperatorIsNotEmpty: 'is not empty',
    filterOperatorIsAnyOf: 'is any of',
  };

  // Wähle die richtige Lokalisierung basierend auf der aktuellen Sprache
  const getLocaleText = () => {
    const lang = i18n.language || 'de';
    if (lang.startsWith('de')) {
      return deLocaleText;
    } else if (lang.startsWith('fr')) {
      return frLocaleText;
    } else if (lang.startsWith('en')) {
      return enLocaleText;
    }
    return deLocaleText; // Fallback auf Deutsch
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      mb: 4,
      px: { xs: 2, sm: 3 },
      boxSizing: 'border-box'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title={t('search.help.tooltip', 'Suchhilfe anzeigen')}>
          <IconButton 
            onClick={() => setShowHelpDialog(true)}
            color="primary"
          >
            <InfoOutlined />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          zoom: SEARCH_PAGE_CONTENT_ZOOM,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 0
        }}
      >

      {/* Suchbereich */}
      <Paper sx={{ 
        width: '100%', 
        mt: 0, 
        p: 3,
        mb: 2
      }}>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TaskSearchBar
            searchChips={searchChips}
            placeholder={t('search.searchPlaceholder')}
            onAddSearchChip={(value) => {
              addSearchChip(String(value).trim());
              setSelectedSearchId('');
            }}
            onRemoveSearchChip={(chip) => {
              removeSearchChip(chip);
              setSelectedSearchId('');
            }}
            conditions={validConditions}
            onRemoveCondition={(conditionId) => {
              removeCondition(conditionId);
              setSelectedSearchId('');
            }}
            onOpenFilter={openFilterDialog}
            filterActive={hasActiveConditions || filterDialogOpen}
            formatConditionLabel={(condition, translate) => formatConditionChipLabel(
              condition,
              translate,
              (field) => {
                const col = allColumns.find((item) => item.field === field);
                return col?.headerName || field;
              }
            )}
            endContent={(
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="saved-search-label">
                  {t('search.savedSearches.label', 'Gespeicherte Suchen')}
                </InputLabel>
                <Select
                  labelId="saved-search-label"
                  value={selectedSearchId}
                  onChange={handleSearchSelect}
                  label={t('search.savedSearches.label', 'Gespeicherte Suchen')}
                  renderValue={(selected) => {
                    if (!selected) return t('search.savedSearches.none', 'Keine Auswahl');
                    const search = savedSearches.find(s => s.id === selected);
                    return search ? search.name : '';
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 400, width: 350 }
                    }
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: 'var(--rd-primary)'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('search.savedSearches.none', 'Keine Auswahl')}</em>
                  </MenuItem>
                  {savedSearches.map((search) => (
                    <MenuItem key={search.id} value={search.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            icon={<StarBorder />}
                            checkedIcon={<Star />}
                            checked={defaultSearchId === search.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDefaultSearch(search.id);
                            }}
                            sx={{ 
                              color: 'orange', 
                              '&.Mui-checked': { color: 'orange' },
                              mr: 1
                            }}
                          />
                          <ListItemText primary={search.name} />
                        </Box>
                        {search.id !== 'search-today' && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSearch(search.id);
                            }}
                            sx={{ 
                              ml: 0.5,
                              color: 'error.main',
                              '&:hover': { 
                                backgroundColor: 'error.light',
                                color: 'error.dark'
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            afterFilterContent={(
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Tooltip title={
                  startDate || endDate
                    ? t('search.dateRangeActive', {
                        start: startDate ? formatDateSync(startDate) : '…',
                        end: endDate ? formatDateSync(endDate) : '…',
                        defaultValue: 'Zeitraum: {{start}} – {{end}}'
                      })
                    : t('search.dateRangeTooltip', 'Zeitraum festlegen')
                }>
                  <IconButton
                    onClick={() => {
                      setDraftStartDate(startDate);
                      setDraftEndDate(endDate);
                      setShowDateRangeDialog(true);
                    }}
                    size="small"
                    aria-label={t('search.dateRangeTooltip', 'Zeitraum festlegen')}
                    sx={{
                      color: (startDate || endDate) ? 'var(--rd-primary)' : 'text.secondary',
                      border: (startDate || endDate) ? '2px solid var(--rd-primary)' : '2px solid transparent',
                      '&:hover': { color: 'var(--rd-primary)' }
                    }}
                  >
                    <DateRange />
                  </IconButton>
                </Tooltip>
                {SHOW_COLUMN_ORDER_DIALOG && (
                  <Tooltip title={t('common.sortColumns', 'Spalten sortieren')}>
                    <IconButton
                      onClick={() => setShowColumnOrderDialog(!showColumnOrderDialog)}
                      size="small"
                      sx={{ color: 'var(--rd-primary)' }}
                    >
                      <DragIndicator />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t('common.manageColumns', 'Spalten verwalten')}>
                  <IconButton
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                    size="small"
                    sx={{ color: 'var(--rd-primary)' }}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
          <TaskFilterDialog
            open={filterDialogOpen}
            anchorEl={filterAnchorEl}
            onClose={closeFilterDialog}
            onApply={(nextConditions) => {
              setConditions(nextConditions);
              setSelectedSearchId('');
            }}
            onSave={handleSaveSearch}
            conditions={conditions}
            columns={allColumns.map((col) => ({ field: col.field, label: col.headerName }))}
            availableStatuses={ALL_REPORT_STATUSES}
          />
        </Box>
      </Paper>

      {/* Ergebnisbereich */}
      <Paper sx={{ 
        width: '100%', 
        height: 'calc(100vh - 270px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* DataGrid */}
            <Box ref={dataGridRef} sx={{ flex: 1, width: '100%', minHeight: 0 }}>
              <DataGrid
                rows={filteredResults}
                columns={dataGridColumnsWithDnd}
                getRowId={(row) => row.exam_id ? `exam-${row.exam_id}` : `report-${row.report_id}`}
                pagination={false}
                hideFooter
                disableColumnMenu
                columnBufferPx={800}
                onColumnWidthChange={handleColumnWidthChange}
                onRowDoubleClick={handleRowDoubleClick}
                localeText={getLocaleText()}
                getRowHeight={() => 'auto'}
                sx={{
                  height: '100%',
                  overflowY: 'auto',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : 'var(--rd-bg-paper)',
                  borderRadius: 2,
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'var(--rd-bg-subtle)',
                    color: (theme) => theme.palette.mode === 'dark' ? theme.palette.text.primary : 'var(--rd-text-primary)',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    borderBottom: (theme) => theme.palette.mode === 'dark' ? '1px solid var(--rd-divider)' : '1px solid var(--rd-divider)',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: '1.2',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                  },
                  '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent) !important',
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'default',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--rd-primary) 4%, transparent)',
                  },
                  '& .MuiDataGrid-cell': {
                    color: (theme) => theme.palette.mode === 'dark' ? theme.palette.text.primary : 'var(--rd-text-primary)',
                    fontSize: '0.98rem',
                    borderBottom: (theme) => theme.palette.mode === 'dark' ? '1px solid var(--rd-divider)' : '1px solid var(--rd-divider)',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    lineHeight: 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: '8px',
                    paddingBottom: '8px'
                  },
                  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflowX: 'auto',
                    overflowY: 'auto'
                  }
                }}
              />
            </Box>
            
            {/* Ergebnisanzahl und Download-Button unter dem DataGrid */}
            {filteredResults.length > 0 && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredResults.length} {filteredResults.length === 1 ? t('search.result', 'Ergebnis') : t('search.results', 'Ergebnisse')}
                  {hasMore && (
                    <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                      ({t('search.limitReached', 'Maximal 500 Ergebnisse')})
                    </Typography>
                  )}
                </Typography>
                <Button
                  onClick={handleExportCSV}
                  startIcon={<FileDownload />}
                  className="button"
                  size="small"
                >
                  {t('search.exportCSV', 'Als CSV exportieren')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
      </Box>

      {/* Warning Dialog für zu viele Ergebnisse */}
      <Dialog open={showWarningDialog} onClose={() => setShowWarningDialog(false)}>
        <DialogTitle>{t('search.tooManyResults.title', 'Zu viele Ergebnisse')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('search.tooManyResults.message', 'Die Suche führt zu mehr als 500 Ergebnissen. Bitte schränken Sie die Suche weiter ein.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarningDialog(false)} className="button">
            {t('common.ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hilfe Dialog */}
      <Dialog open={showHelpDialog} onClose={() => setShowHelpDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('search.help.title', 'Suche mit Chips')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              {t('search.help.line1', '• Ohne *: der Begriff wird überall gesucht (Schmidt findet auch „Schmidtchen“).')}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              {t('search.help.line2', '• Mit *: beliebige Zeichen, z. B. D*mo findet Demo, M*er findet Mayer, Maier, Meier, Meyer.')}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              {t('search.help.line3', '• Mehrere Chips werden mit UND verknüpft.')}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
              {t('search.help.info', 'Die Suche durchsucht alle Spalten (Groß-/Kleinschreibung wird ignoriert). Filterbedingungen aus dem Dialog gelten zusätzlich auf den Ergebnissen.')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHelpDialog(false)} className="button">
            {t('common.close', 'Schließen')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDateRangeDialog}
        onClose={() => setShowDateRangeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('search.dateRangeTitle', 'Zeitraum')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
            <CustomDatePicker
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              label={t('search.startDate', 'Von')}
              sx={{ width: 220 }}
            />
            <Typography variant="body2" color="text.secondary">
              {t('search.to', 'bis')}
            </Typography>
            <CustomDatePicker
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              label={t('search.endDate', 'Bis')}
              sx={{ width: 220 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            className="button-cancel"
            onClick={() => {
              setDraftStartDate('');
              setDraftEndDate('');
            }}
          >
            {t('search.dateRangeClear', 'Zurücksetzen')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              className="button-cancel"
              onClick={() => setShowDateRangeDialog(false)}
            >
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button
              className="button"
              onClick={() => {
                const datesChanged = draftStartDate !== startDate || draftEndDate !== endDate;
                setStartDate(draftStartDate);
                setEndDate(draftEndDate);
                if (datesChanged) {
                  setSelectedSearchId('');
                }
                setShowDateRangeDialog(false);
              }}
            >
              {t('common.apply', 'Anwenden')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Spalten-Verwaltungs-Dialog */}
      <Dialog
        open={showColumnSelector}
        onClose={() => {
          setShowColumnSelector(false);
          setColumnSearchTerm('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          {t('common.manageColumns', 'Spalten verwalten')}
        </DialogTitle>
        <DialogContent sx={{ height: '450px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Suchfeld für Spalten */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('common.searchColumns', 'Spalten suchen...')}
            value={columnSearchTerm}
            onChange={(e) => setColumnSearchTerm(e.target.value)}
            sx={{ mb: 2, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ 
            display: 'inline-grid',
            gridTemplateColumns: 'repeat(3, minmax(220px, max-content))',
            gridAutoRows: '40px',
            gap: 2, 
            mt: 1,
            overflow: 'auto',
            flex: 1,
            justifyContent: 'start',
            justifyItems: 'start',
            alignItems: 'start',
          }}>
            {defaultColumnOrder
              .filter(field => {
                if (!columnSearchTerm) return true;
                const searchLower = columnSearchTerm.toLowerCase();
                const fieldName = field.toLowerCase();
                const translatedName = t(`search.columns.${field}`, field).toLowerCase();
                return fieldName.includes(searchLower) || translatedName.includes(searchLower);
              })
              .sort((a, b) => {
                const labelA = t(`search.columns.${a}`, a).toLowerCase();
                const labelB = t(`search.columns.${b}`, b).toLowerCase();
                return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
              })
              .map((field) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={columnVisibilityModel[field] !== false}
                      onChange={(e) => {
                        const newModel = {
                          ...columnVisibilityModel,
                          [field]: e.target.checked
                        };
                        setColumnVisibilityModel(newModel);
                      }}
                    />
                  }
                  label={t(`search.columns.${field}`, field)}
                  sx={{ m: 0 }}
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowColumnSelector(false);
              setColumnSearchTerm('');
            }}
            className="button-cancel"
          >
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button 
            onClick={() => {
              handleColumnVisibilityChange(columnVisibilityModel);
              setShowColumnSelector(false);
              setColumnSearchTerm('');
            }}
            className="button"
          >
            {t('common.apply', 'Anwenden')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spaltenreihenfolge-Dialog */}
      <ColumnOrderDialog
        open={showColumnOrderDialog}
        onClose={() => setShowColumnOrderDialog(false)}
        columns={(() => {
          const orderedFields = columnOrder.length > 0 ? columnOrder : defaultColumnOrder;
          return orderedFields
            .map(field => allColumns.find(col => col.field === field))
            .filter(col => col && columnVisibilityModel[col.field] !== false);
        })()}
        onSave={handleColumnOrderSave}
      />

      {/* Kontextmenü */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleOpenViewer}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('search.contextMenu.openViewer', 'Viewer öffnen')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrintFromContext}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('search.contextMenu.print', 'Drucken')}</ListItemText>
        </MenuItem>
                        <MenuItem onClick={handleResendFromContext}>
                          <ListItemIcon>
                            <SendIcon fontSize="small" />
                          </ListItemIcon>
                            <ListItemText>{t('search.contextMenu.resendDicom', 'Erneut als DICOM senden')}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleHl7ResendFromContext}>
                          <ListItemIcon>
                            <SendIcon fontSize="small" />
                          </ListItemIcon>
                            <ListItemText>{t('search.contextMenu.resendHl7', 'Erneut als HL7 senden')}</ListItemText>
                        </MenuItem>
        {currentUser && 
         currentUser.group_level >= 1 && 
         currentUser.group_level <= 6 && 
         contextMenu?.row?.report_status === 'APPROVED' && (
          <MenuItem onClick={handleAddendumFromContext}>
            <ListItemIcon>
              <AddendumIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('search.contextMenu.addendum', 'Addendum')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDicomPdfFromContext}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('search.contextMenu.dicomPdf', 'DICOM PDF Rendern')}</ListItemText>
        </MenuItem>
        {aiUserPrompts.some(p => p.show_in_search) && contextMenu?.row?.patient_id && (
          <MenuItem onClick={handleAiSubMenuOpen}>
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" sx={{ color: 'var(--rd-primary)' }} />
            </ListItemIcon>
            <ListItemText>{t('aiSummary.contextMenuTitle', 'KI Zusammenfassung')}</ListItemText>
            <ArrowRightIcon fontSize="small" sx={{ ml: 1 }} />
          </MenuItem>
        )}
      </Menu>
      {/* AI Summary Sub-Menü */}
      <Menu
        open={Boolean(aiSubMenuAnchor)}
        anchorEl={aiSubMenuAnchor}
        onClose={handleAiSubMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {aiUserPrompts.filter(p => p.show_in_search).map((prompt) => (
          <MenuItem key={prompt.id} onClick={() => handleOpenAiSummaryPopup(prompt.name, prompt.prompt, prompt.scope)}>
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" sx={{ color: 'var(--rd-primary)' }} />
            </ListItemIcon>
            <ListItemText>{prompt.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* PDF Preview Dialog - Freitext */}
      <PDFPreviewDialog
        open={pdfPreviewOpen}
        onClose={() => {
          console.log('📝 FREETEXT PDF DIALOG wird geschlossen');
          setPdfPreviewOpen(false);
          setPdfData(null);
          setHighlightTerms([]);
        }}
        pdfData={pdfData}
        reportTitle={t('search.pdfPreview', 'Befund-Vorschau')}
        highlightTerms={highlightTerms}
      />

      {/* PDF Preview Dialog - Strukturiert */}
      <PDFPreviewDialogStructured
        open={pdfPreviewStructuredOpen}
        onClose={() => {
          console.log('📋 STRUCTURED PDF DIALOG wird geschlossen');
          setPdfPreviewStructuredOpen(false);
          setPdfData(null);
          setHighlightTerms([]);
        }}
        pdfData={pdfData}
        reportTitle={t('search.pdfPreviewStructured', 'Strukturierter Befund-Vorschau')}
        highlightTerms={highlightTerms}
      />

      {/* DICOM PDF Viewer für Test */}
      <DicomPDFViewer
        open={dicomPdfOpen}
        onClose={() => {
          setDicomPdfOpen(false);
          setDicomPdfReportId(null);
        }}
        reportId={dicomPdfReportId}
      />

      {/* Addendum Bestätigungsdialog */}
      <Dialog
        open={addendumDialogOpen}
        onClose={handleAddendumCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('search.addendum.dialogTitle', 'Addendum erstellen')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('search.addendum.confirmMessage', 'Sind Sie sicher, dass Sie diesen Befund erneut öffnen wollen? Er durchläuft danach den Freigabeprozess erneut.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            className="button-cancel"
            onClick={handleAddendumCancel}
          >
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button
            className="button"
            onClick={handleAddendumConfirm}
          >
            {t('common.open', 'Öffnen')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Search; 